import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, X } from "@phosphor-icons/react";
import PasswordInput from "@/components/PasswordInput";

export const MfaSettingsCard = () => {
  const [status, setStatus] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/mfa/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/mfa/reset", { password: pw });
      toast.success("MFA reset — you'll set it up again at your next sign-in.");
      setResetOpen(false); setPw(""); load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const methodLabel = status.method === "sms"
    ? `Text message to ${status.phone_hint || "your phone"}`
    : "Authenticator app";

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="mfa-settings-card">
      <div className="flex items-center gap-2">
        <ShieldCheck size={20} weight="fill" className={status.enabled ? "text-green-600" : "text-amber-500"} />
        <div className="tinylabel">Two-factor authentication</div>
      </div>
      {status.enabled ? (
        <div className="mt-2">
          <div className="text-sm">
            <span className="inline-block px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-green-100 text-green-800 mr-2" data-testid="mfa-status-badge">Enabled</span>
            {methodLabel}
            {status.enrolled_at && <span className="text-slate-400"> · since {new Date(status.enrolled_at).toLocaleDateString()}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Recovery codes remaining: <b>{status.recovery_codes_left}</b>
          </div>
          <button type="button" onClick={() => { setResetOpen(true); setPw(""); }}
            data-testid="mfa-reset-btn"
            className="mt-3 h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50">
            Reset my 2FA (change method / new phone)
          </button>
          <div className="text-xs text-slate-400 mt-2">
            Resetting also signs this browser out of "remembered device". You'll walk through setup again at your next sign-in.
          </div>
        </div>
      ) : (
        <div className="mt-2 text-sm text-slate-600" data-testid="mfa-status-pending">
          <span className="inline-block px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-amber-100 text-amber-800 mr-2">Pending</span>
          Two-factor authentication is required for admin accounts. You'll be walked
          through a one-minute setup the next time you sign in.
        </div>
      )}

      {resetOpen && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" onClick={() => setResetOpen(false)}>
          <form onSubmit={doReset} onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-md w-full max-w-sm border border-slate-200 p-6" data-testid="mfa-reset-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold">Reset two-factor authentication</div>
              <button type="button" onClick={() => setResetOpen(false)}><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-3">
              Confirm your password. Your current method and recovery codes will be wiped,
              and you'll set 2FA up fresh at your next sign-in.
            </p>
            <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} required
              data-testid="mfa-reset-password"
              className="w-full h-11 px-3 border border-slate-300 rounded-md" />
            <button disabled={busy || !pw} data-testid="mfa-reset-confirm"
              className="mt-4 w-full h-11 bg-red-600 hover:bg-red-700 text-white font-bold rounded-md disabled:opacity-60">
              {busy ? "Resetting…" : "Reset 2FA"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
